import { useDispatch, useSelector } from '@redux';
import { Modal, Tag } from 'antd';
import { ORDER_EXCHANGE_STATUS } from 'constants/ordersExchange';
import RestShow from 'containers/rest/Show';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import ShowContent from './ShowContent';
import ConfirmPaidModal from '../components/Modal/ConfirmPaidModal';
import CancelOrderModal from '../components/Modal/CancelOrderModal';
import EditShippingAddressModal from '../components/Modal/EditShippingAddressModal';
import { changePaymentToBank } from '@redux/ordersExchange/actions';
import { PAYMENT_STATUSES, PAYMENT_TYPES } from 'constants/constants';
// import ConfirmPaidModal from './ConfirmPaidModal';
// import CancelOrderModal from './CancelOrderModal';
// import EditShippingAddressModal from '../components/Modal/EditShippingAddressModal';

const TYPE = {
  cancelOrder: ['AWAITING_CONFIRMATION', 'CONFIRMED', 'RETURNING'],
  confirmPaid: ['AWAITING_CONFIRMATION'],
  editShippingInfo: ['AWAITING_CONFIRMATION'],
  editReturnInfo: ['AWAITING_CONFIRMATION', 'CONFIRMED', 'RETURNING'],
  changePaymentToBank: ['AWAITING_CONFIRMATION'],
};

const KEY_PATH = {
  AWAITING_CONFIRMATION: {
    title: 'Awaiting Confirmation',
    keyPath: 'AWAITING_CONFIRMATION',
  },
  CONFIRMED: {
    title: 'Confirmed',
    keyPath: 'CONFIRMED',
  },
  RETURNING: {
    title: 'Returning',
    keyPath: 'RETURNING',
  },
  PROCESSING: {
    title: 'Processing',
    keyPath: 'PROCESSING',
  },
  COMPLETED: {
    title: 'Completed',
    keyPath: 'COMPLETED',
  },
  CANCELLED: {
    title: 'Cancelled',
    keyPath: 'CANCELLED',
  },
};

const EXTRA_PARAMS = {
  join: [
    'customer',
    'newOrder',
    'newOrder.payments',
    'newOrder.items',
    'orderReturn',
    'originalOrder',
    'originalOrder.items',
    'histories',
    'histories.creator',
    'orderReturn.shippingCarrier',
    'newOrder.shippingCarrier',
  ],
  includeErpAmount: true,
};

const API_OPTIONS = {
  extraParams: EXTRA_PARAMS,
  isRequestApi: true,
};

const Show = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const cancelOrderModalRef = React.useRef(null);

  const data = useSelector<DynamicObjectType>(
    (state) => state.ordersExchange.currentData,
  );
  const loading = useSelector((state) => state.orders.loading);

  const [confirmPaidModal, setConfirmPaidModal] = useState(false);
  const [editShippingAddressModal, setEditShippingAddressModal] =
    useState(false);
  const [editReturnInforModal, setEditReturnInforModal] = useState(false);

  const paymentMethod = useMemo(() => {
    const OPTIONS = [
      ...PAYMENT_TYPES,
      { value: 'bank', text: 'Bank' },
      { value: 'other', text: 'Other' },
    ];
    if (data?.newOrder?.payments?.length) {
      return data.newOrder.payments
        .filter((e) => e.type !== 'point')
        .map(
          (payment) =>
            OPTIONS.find((e) => e.value === payment.type)?.value ||
            payment?.paymentDetail?.method,
        )
        .join(', ');
    }
    return OPTIONS.find((e) => e.value === data?.paymentType)?.text;
  }, [data]);

  const paymentStatus = useMemo(() => {
    return PAYMENT_STATUSES.find(
      (e) =>
        e.value ===
        data?.newOrder?.payments?.filter((e) => e.type !== 'point')?.[0]
          ?.status,
    )?.value;
  }, [data]);

  const EXTRA_OPTIONS = useMemo(() => {
    return [
      {
        ...(TYPE['cancelOrder'].includes(data?.status)
          ? {
              label: 'Cancel order',
              key: 'cancelOrder',
            }
          : null),
      },
      {
        ...(TYPE['confirmPaid'].includes(data?.status) &&
        paymentStatus !== 'paid'
          ? {
              label: 'Confirm paid',
              key: 'confirmPaid',
            }
          : null),
      },
      {
        ...(TYPE['changePaymentToBank'].includes(data?.status) &&
        paymentMethod !== 'bank' &&
        paymentStatus !== 'paid'
          ? { label: 'Change payment to bank', key: 'changePaymentToBank' }
          : null),
      },
      // {
      //   ...(TYPE['editShippingInfo'].includes(data?.status)
      //     ? {
      //         label: 'Edit shipping information',
      //         key: 'editShippingInfo',
      //       }
      //     : null),
      // },
      // {
      //   ...(TYPE['editReturnInfo'].includes(data?.status)
      //     ? {
      //         label: 'Edit return information',
      //         key: 'editReturnInfo',
      //       }
      //     : null),
      // },
    ];
  }, [data]);

  const handleParamsBreadcrumb = useMemo(() => {
    if (!KEY_PATH?.[data?.status]) {
      return {
        title: 'All',
        href: '/orders-exchange',
      };
    }
    return {
      title: KEY_PATH?.[data?.status]?.title,
      href: `/orders-exchange?customLayout=${data?.status}&filters=${encodeURIComponent(
        JSON.stringify(
          [
            {
              condition: 'and',
              key: 'status',
              operator: 'is',
              value: data?.status,
            },
          ],
          null,
          0,
        ),
      )}`,
    };
  }, [data?.status]);

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'common.page.orders',
        href: '/orders-exchange',
      },
      {
        title: handleParamsBreadcrumb?.title,
        href: handleParamsBreadcrumb?.href,
      },
      {
        title: (
          <span className="font-bold text-sm text-[--primary]">
            {`#${data?.exchangeNumber}`}
          </span>
        ),
      },
    ],
    [data?.exchangeNumber, handleParamsBreadcrumb],
  );

  const cancelOrder = () => {
    cancelOrderModalRef?.current?.openModal();
  };

  const onChangePaymentTobank = useCallback(() => {
    Modal.confirm({
      title: t('ordersExchange.changePaymentToBank'),
      content: t('ordersExchange.changePaymentToBankContent'),
      okButtonProps: {
        loading: loading,
      },
      onOk: async () => {
        try {
          await dispatch(
            changePaymentToBank({
              data: {
                id: data?.id,
                exchangeId: data?.id,
              },
              options: {
                isShowSuccessNoti: true,
              },
            }),
          );
        } catch (error) {
          // console.log('error', error);
        }
      },
    });
  }, [data?.id, dispatch, loading, t]);

  const handleExtraOptions = (e) => {
    switch (e.key) {
      case 'cancelOrder': {
        return cancelOrder();
      }
      case 'confirmPaid': {
        return setConfirmPaidModal(true);
      }
      case 'editShippingInfo': {
        return setEditShippingAddressModal(true);
      }
      case 'editReturnInfo': {
        return setEditReturnInforModal(true);
      }
      case 'changePaymentToBank': {
        return onChangePaymentTobank();
      }
    }
  };

  const tempOrderStatus = ORDER_EXCHANGE_STATUS[data?.status];

  return (
    <>
      <RestShow
        model="ordersExchange"
        breadcrumbs={breadcrumbs}
        extraBreadcrumb={
          <Tag
            color={ORDER_EXCHANGE_STATUS[data?.status]?.background}
            style={{
              borderRadius: 5,
              padding: '5px 10px',
              border: 'none',
              marginLeft: 10,
            }}
          >
            <div
              style={{
                color: ORDER_EXCHANGE_STATUS[data?.status]?.color,
              }}
            >
              {t(tempOrderStatus?.text)}
            </div>
          </Tag>
        }
        extraOptions={EXTRA_OPTIONS}
        handleExtraOptions={handleExtraOptions}
        apiOptions={API_OPTIONS}
      >
        <ShowContent data={data} />
      </RestShow>
      <ConfirmPaidModal
        id={data?.newOrder?.id}
        visible={confirmPaidModal}
        onCancel={() => setConfirmPaidModal(false)}
        exchangeId={data?.id}
      />
      <CancelOrderModal ref={cancelOrderModalRef} data={data} />
      <EditShippingAddressModal
        visible={editShippingAddressModal}
        onCancel={() => setEditShippingAddressModal(false)}
        title="Edit Shipping Information"
        name="newOrder"
        rootName="address"
      />
      <EditShippingAddressModal
        visible={editReturnInforModal}
        onCancel={() => setEditReturnInforModal(false)}
        title="Edit Return Information"
        name="orderReturn"
        rootName="pickupInformation"
      />
    </>
  );
};
export default Show;
